import { Css, FormSectionLayout, StatusBanner, StatusBannerProps, useComputed } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { useRouteParams } from "src/hooks/useRouteParams";
import { OptionTags } from "src/routes/projects/change-events/guided/workflows/components/optionCardUtils";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { OptionsFilterBar } from "src/routes/projects/change-events/guided/workflows/components/OptionsFilterBar";
import { ConfigurationTrueUpFormInput } from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/configurationTrueUpWorkflow";
import {
  createDiscontinuedOptionGroups,
  createDiscontinuedOptionsRows,
  DiscontinuedOptionsRowsInput,
  droppedOptionIdsOf,
  hasDroppedToggle,
  mapDiscontinuedOptionsRowsToForm,
  mapDiscontinuedOptionsRowsToInput,
  OptionGroupChangeRow,
  useSaveDiscontinuedOptions,
} from "src/routes/projects/change-events/guided/workflows/configurationTrueUp/discontinuedOptionsUtils";
import {
  createOptionGroupSection,
  SelectOptionsFormInput,
  selectOptionsRowsFormConfig,
  showGroup,
  sortGroupOptions,
  toSelectOptionsSections,
} from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils";
import { SelectOptionsUtils_OptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/sharedSteps/selectOptionsUtils.generated";
import { ProjectParams } from "src/routes/routesDef";
import { count, fail, pluralize, queriesResult, toSentence } from "src/utils";
import { useFormState } from "src/utils/formState";
import {
  DiscontinuedOptionsStep_ConfigChangeFragment,
  useDiscontinuedOptionsStepOriginalQuery,
  useDiscontinuedOptionsStepQuery,
} from "./2.DiscontinuedOptionsStep.generated";

type DiscontinuedOptionsStepProps = { formState: ObjectState<ConfigurationTrueUpFormInput> };

/**
 * Lists the option groups the move to the new Product Offering version changed, so the user can keep a discontinued
 * selection as a custom option, swap in an available alternative, or remove it when the group is optional.
 */
export function DiscontinuedOptionsStep(props: DiscontinuedOptionsStepProps) {
  const { formState } = props;
  const { projectId } = useRouteParams<ProjectParams>();
  const changeEventId = formState.id.value ?? fail("Configuration True-Up draft was not created");
  const variables = { changeEventId, structureId: projectId };
  const query = useDiscontinuedOptionsStepQuery({ variables });
  // Fetch `original` with `no-cache`: it shares option group and option ids with `result`, so caching both in Apollo
  // would let `original`'s option groups overwrite `result`'s.
  const originalQuery = useDiscontinuedOptionsStepOriginalQuery({ variables, fetchPolicy: "no-cache" });
  return queriesResult([query, originalQuery] as const, {
    data: ({ changeEvent }, originalData) => {
      const configChange = changeEvent.configChange ?? fail("Change event has no config change");
      const original = originalData.changeEvent.configChange?.original ?? fail("Change event has no config change");
      return (
        <DiscontinuedOptionsStepView
          formState={formState}
          configChange={configChange}
          originalGroups={original.config.optionGroups}
        />
      );
    },
  });
}

type DiscontinuedOptionsStepViewProps = {
  formState: ObjectState<ConfigurationTrueUpFormInput>;
  configChange: DiscontinuedOptionsStep_ConfigChangeFragment;
  originalGroups: SelectOptionsUtils_OptionGroupFragment[];
};

function DiscontinuedOptionsStepView(props: DiscontinuedOptionsStepViewProps) {
  const { formState, configChange, originalGroups } = props;
  const { result, optionGroupChanges } = configChange;
  const [showExpired, setShowExpired] = useState(false);
  const priceMode = useComputed(() => formState.priceMode.value ?? OptionPriceMode.Price, [formState]);
  const rows = useMemo(() => createDiscontinuedOptionsRows(optionGroupChanges), [optionGroupChanges]);
  const changedGroups = useMemo(
    () => createDiscontinuedOptionGroups(rows, result.config.optionGroups, originalGroups),
    [rows, result, originalGroups],
  );
  // Sort once on first load (selected first, then checkout order), then keep that order on every update, like
  // checkout does, so picking an option doesn't move its card to the top of its group
  const [firstSort] = useState(() => sortGroupOptions(changedGroups));
  const groups = useMemo(() => sortGroupOptions(changedGroups, firstSort), [changedGroups, firstSort]);
  const { optionIds, droppedOptionIds } = useComputed(
    () => ({
      optionIds: formState.configChange.optionIds.value,
      droppedOptionIds: formState.configChange.droppedOptionIds.value,
    }),
    [formState],
  );
  const rowsInput = useMemo(() => ({ groups, optionIds, droppedOptionIds }), [groups, optionIds, droppedOptionIds]);
  // The cards are locked while a save is in flight, so a pick can't change under it
  const { save, saving } = useSaveDiscontinuedOptions(formState);
  const rowsForm = useFormState({
    config: selectOptionsRowsFormConfig,
    // Re-maps whenever a save (the cards' or the Right Side pane's) updates `formState`
    init: {
      input: rowsInput,
      map: (input: DiscontinuedOptionsRowsInput) =>
        mapDiscontinuedOptionsRowsToForm(input, rows, input.droppedOptionIds),
    },
    readOnly: saving,
    autoSave: async () => {
      moveDependents(rows, groups, rowsForm.rows.rows);
      const formRows = rowsForm.rows.value;
      await save(
        mapDiscontinuedOptionsRowsToInput(formState, formRows, groups, rows),
        droppedOptionIdsOf(rows, formRows),
      );
      // Reset the cards to what's saved in `formState` (the old selection if the save failed), so the next change
      // always counts as a change. Revert first, since an update skips rows with unsaved edits.
      rowsForm.revertChanges();
      rowsForm.update({
        groups,
        optionIds: formState.configChange.optionIds.value,
        droppedOptionIds: formState.configChange.droppedOptionIds.value,
      });
    },
  });

  const rowByRpogId = rowsForm.rows.rows.keyBy((row) => row.groupId.value);
  const rowFor = (rpogId: string) => rowByRpogId[rpogId] ?? fail(`No row for group ${rpogId}`);

  const decorations = useComputed(() => createDecorations(rows, groups, rowsForm.rows.value), [rows, groups, rowsForm]);

  const sections = toSelectOptionsSections(groups, result.interiorSubSteps).map((section) => ({
    title: section.title,
    childSections: section.groups
      .filter((group) => showGroup(group, rowFor(group.group.id).value, showExpired))
      .map((group) =>
        createOptionGroupSection(group, groups, rowFor, priceMode, {
          optionTags: decorations.optionTags,
          toggle: hasDroppedToggle(group, rows),
          footer: decorations.banners[group.group.id]?.map((p) => <StatusBanner key={p.title} {...p} />),
        }),
      ),
  }));

  return (
    <FormSectionLayout
      title="Discontinued Options"
      description="Review selections that are no longer available in the latest Product Offering. Keep existing selections as custom options, replace them with available alternatives, or remove optional selections."
      withJumpLinks
      withAutoSave
      initialFields={
        <div css={Css.df.fdc.gap2.$}>
          {rows.nonEmpty && (
            <StatusBanner
              type="info"
              title="Some Options No Longer Available"
              description="Selections discontinued in the latest Product Offering can be kept as project-specific custom options. Custom options preserve the existing selection but cannot be modified through Product Swap in the future."
            />
          )}
          <OptionsFilterBar
            showExpired={showExpired}
            onShowExpired={setShowExpired}
            priceMode={priceMode}
            onPriceMode={(mode) => formState.priceMode.set(mode)}
          />
        </div>
      }
      sections={sections.filter((section) => section.childSections.nonEmpty)}
    />
  );
}

/**
 * When a prerequisite is kept or removed, its dependents follow it:
 * - a single-option add-on (e.g. "Walk-in Closet"), or a product whose option the new version removed, is checked or
 *   unchecked with it, and stays locked;
 * - an elevation, exterior scheme, package, personalization variant or product with a replacement switches between
 *   its original option and the new version's option, and the user can still change it afterwards.
 */
function moveDependents(
  rows: OptionGroupChangeRow[],
  groups: SelectOptionsUtils_OptionGroupFragment[],
  formRows: readonly ObjectState<SelectOptionsFormInput>[],
): void {
  const formRowByRpogId = formRows.keyBy((row) => row.groupId.value);
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  const isKept = (row: OptionGroupChangeRow, value: "value" | "originalValue") => {
    const formRow = formRowByRpogId[row.rpogId];
    return !!formRow?.selected[value] && formRow.optionId[value] === row.rpoId;
  };
  const queue = rows.filter((row) => isKept(row, "value") !== isKept(row, "originalValue"));
  const visitedRpoIds = new Set<string>();
  while (queue.nonEmpty) {
    const prerequisite = queue.shift()!;
    if (visitedRpoIds.has(prerequisite.rpoId)) continue;
    visitedRpoIds.add(prerequisite.rpoId);
    const kept = isKept(prerequisite, "value");
    rows
      .filter((row) => row.prerequisite?.rpoId === prerequisite.rpoId && isKept(row, "value") !== kept)
      .forEach((row) => {
        const formRow = formRowByRpogId[row.rpogId] ?? fail(`No row for group ${row.rpogId}`);
        const group = groupByRpogId[row.rpogId] ?? fail(`No group ${row.rpogId}`);
        if (hasDroppedToggle(group, rows)) formRow.selected.set(kept, { autoSave: false });
        else formRow.optionId.set(kept ? row.rpoId : row.change.targetOption?.readyPlanOption.id, { autoSave: false });
        queue.push(row);
      });
  }
}

/**
 * The tags and banners for each row, based on its current selection:
 * - unchecked, or a single-select dependent whose prerequisite was removed: "Removed" and the "Removed" banner;
 * - kept: "Discontinued" (but not on a dependent) and the "kept as a custom option" banner;
 * - replaced with an alternative: just the "Removed" tag;
 * - a prerequisite that isn't kept (unchecked or replaced) also warns that its dependents are removed with it.
 *
 * Banners are keyed by the group that owns the section, so a child group's banners show under its parent.
 */
function createDecorations(
  rows: OptionGroupChangeRow[],
  groups: SelectOptionsUtils_OptionGroupFragment[],
  formRows: SelectOptionsFormInput[],
): { optionTags: OptionTags; banners: Record<string, StatusBannerProps[]> } {
  const groupByRpogId = groups.keyBy((ccfgog) => ccfgog.group.id);
  const formRowByRpogId = formRows.keyBy((row) => row.groupId);
  const optionTags: OptionTags = {};
  const banners: Record<string, StatusBannerProps[]> = {};
  const isKept = (row: OptionGroupChangeRow) => {
    const formRow = formRowByRpogId[row.rpogId];
    return !!formRow?.selected && formRow.optionId === row.rpoId;
  };
  rows.forEach((row) => {
    const name = row.change.originalRpov?.name;
    const sectionId = groupByRpogId[row.rpogId]?.parentGroup?.id ?? row.rpogId;
    const addBanner = (banner: StatusBannerProps) => (banners[sectionId] ??= []).push(banner);
    const kept = isKept(row);
    if (!kept) {
      optionTags[row.rpoId] = { text: "Removed", type: "error" };
    } else if (!row.prerequisite) {
      // A dependent's option is still in the new version, so only its prerequisite is discontinued
      optionTags[row.rpoId] = { text: "Discontinued", type: "neutral" };
    }
    // Unchecked, or (for a single-select dependent) moved off its original option because its prerequisite was removed
    if (!formRowByRpogId[row.rpogId]?.selected || (!kept && !!row.prerequisite && !isKept(row.prerequisite))) {
      addBanner({ type: "error", title: `Removed ‘${name}’.` });
    }
    if (kept) {
      addBanner({ type: "info", title: `‘${name}’ is being kept as a custom option.` });
    }
    // Unchecking a prerequisite and swapping a pick-one one for an alternative both take its dependents with it
    // Named rather than "below", since a dependent can live in a different section than its prerequisite
    const dependents = rows.filter((other) => other.prerequisite?.rpoId === row.rpoId);
    if (!kept && dependents.nonEmpty) {
      const names = toSentence(dependents.map((dependent) => `‘${dependent.change.originalRpov?.name}’`));
      addBanner({
        type: "error",
        title: `${count(dependents, "dependent selection")} will also be removed`,
        description: `Removing ‘${name}’ will also remove ${names} because ${pluralize(dependents, "it requires", "they require")} this option.`,
      });
    }
  });
  return { optionTags, banners };
}
